import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Link as ReactRouterLink } from 'react-router-dom';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import { Typography, Box, Grid } from '@mui/material';
import Container from 'components/Container';
import ContainerBox from 'components/ContainerBox';
import LinkButton from 'common/components/LinkButton';
import { SupportArticles } from 'common/modules/support';
import DeliveryOptionsTable from './components/DeliveryOptionsTable';
import CarriersTable from './components/CarriersTable';

import { Routes } from 'routing';
import { useTranslation } from 'services/localization/useTranslation';
import { DeliveryOptionWithProducts } from 'common/types';

import * as ShopSelectors from 'selectors/ShopSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as UserSelectors from 'selectors/UserSelectors';
import { Trans } from 'services/localization';
import ExternalLink from 'components/ExternalLink';
import SetPageTitle from 'components/Portals/SetPageTitle';
import Banner from 'components/Banner';
import { areAllDeliveryOptionsHidden } from 'common/modules/delivery/utils';

const DeliveryHome = () => {
	const classes = useStyles();
	const { t } = useTranslation();

	const activeLocationDeliveryOptions = useSelector(ShopSelectors.activeLocationDeliveryOptions);
	const shopCarriers = useSelector(ShopSelectors.activeShopCarriers);
	const activeLocationId = useSelector(UserSelectors.userActiveLocationId)!;
	const isDeliveryOnlyStore = useSelector(ShopSelectors.isDeliveryOnlyStore);

	const activeLocationCarrier = shopCarriers.filter((carriers) =>
		carriers.locationIds.includes(activeLocationId),
	);

	const activeLocationProducts = useSelector(StockSelectors.activeLocationProducts);

	const isDeliveryOptionsEmpty = activeLocationDeliveryOptions.length === 0;
	const isCarriersEmpty = activeLocationCarrier.length === 0;

	const deliveryOptionsWithProducts: DeliveryOptionWithProducts[] = useMemo(
		() =>
			activeLocationDeliveryOptions.map((deliveryOption) => ({
				...deliveryOption,
				products: activeLocationProducts.filter((p) =>
					(p?.deliveryOptionIds ?? []).includes(deliveryOption.id),
				),
			})),
		[activeLocationDeliveryOptions, activeLocationProducts],
	);

	const noVisibleDeliveryOptions = areAllDeliveryOptionsHidden(activeLocationDeliveryOptions);

	return (
		<>
			<SetPageTitle title={t('delivery.delivery', 'Delivery')} priority={1} />
			<Container>
				{isDeliveryOnlyStore && noVisibleDeliveryOptions && (
					<Banner
						variant="warning"
						title={t(
							'delivery.deliveryOnlyStoreWarningTitle',
							`Your store is on delivery only mode`,
						)}
						description={
							<span>
								<Trans
									i18nKey="delivery.deliveryOnlyStoreWarningDesc"
									defaults="No products will be shown in the online store until you have at least one visible delivery option with products. <settingsLink>Disable delivery only mode</settingsLink>"
									components={{
										settingsLink: (
											<ReactRouterLink
												to={Routes.StoreTab('online-store')}
												className={classes.link}
											>
												{t('delivery.disableDeliveryOnlyMode', 'Disable delivery only mode')}
											</ReactRouterLink>
										),
									}}
								/>
							</span>
						}
					/>
				)}
				<ContainerBox className={classes.containerBox}>
					<Box padding={4}>
						<Grid container justifyContent="space-between" spacing={2}>
							<Grid item xs={12} sm={6}>
								<Typography variant="h5">
									{t('delivery.deliveryOptions', 'Delivery options')}
								</Typography>
								<Typography variant="body2" color="secondary">
									<Trans
										i18nKey="delivery.deliveryOptionsDescription"
										defaults="Manage the <supportLink>delivery options</supportLink> offered in your store"
										components={{
											supportLink: (
												<ExternalLink href={SupportArticles.DELIVERY__SETTING_UP_LOCAL_DELIVERIES}>
													{'delivery options'}
												</ExternalLink>
											),
										}}
									/>
								</Typography>
							</Grid>
							<Grid
								item
								xs={12}
								sm={isDeliveryOptionsEmpty ? 12 : 'auto'}
								className={classes.button}
							>
								<Box pt={isDeliveryOptionsEmpty ? 4 : 0}>
									<LinkButton
										variant={isDeliveryOptionsEmpty ? 'contained' : 'outlined'}
										color="primary"
										to={Routes.NEW_DELIVERY}
									>
										{t('delivery.newDeliveryOption', 'New delivery option')}
									</LinkButton>
								</Box>
							</Grid>
						</Grid>
					</Box>
					{!isDeliveryOptionsEmpty && (
						<DeliveryOptionsTable deliveryOptions={deliveryOptionsWithProducts} />
					)}
				</ContainerBox>
				<ContainerBox className={classes.containerBox}>
					<Box padding={4}>
						<Grid container justifyContent="space-between" spacing={2}>
							<Grid item xs={12} sm={6}>
								<Typography variant="h5">{t('delivery.carriers', 'Carriers')}</Typography>
								<Typography variant="body2" color="secondary">
									<Trans
										i18nKey="delivery.carriersDescription"
										defaults="Define the <supportLink>carriers</supportLink> that handle your deliveries"
										components={{
											supportLink: (
												<ExternalLink href={SupportArticles.DELIVERY__MANAGING_DELIVERY_CARRIERS}>
													{'carriers'}
												</ExternalLink>
											),
										}}
									/>
								</Typography>
							</Grid>
							<Grid item xs={12} sm={isCarriersEmpty ? 12 : 'auto'} className={classes.button}>
								<Box pt={isCarriersEmpty ? 4 : 0}>
									<LinkButton
										variant={isCarriersEmpty ? 'contained' : 'outlined'}
										color="primary"
										to={Routes.NEW_CARRIER}
									>
										{t('delivery.newCarrier', 'New carrier')}
									</LinkButton>
								</Box>
							</Grid>
						</Grid>
					</Box>
					{!isCarriersEmpty && <CarriersTable carriers={activeLocationCarrier} />}
				</ContainerBox>
			</Container>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		containerBox: {
			padding: 0,
		},
		button: {},
		locationContainer: {
			marginTop: theme.spacing(3),
			[theme.breakpoints.up('sm')]: {
				margin: 'unset',
				maxWidth: 225,
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'flex-end',
				'& > *': {
					width: '100%',
				},
			},
		},
		link: {
			color: theme.palette.primary.main,
			textDecoration: 'underline',
		},
	}),
);

export default React.memo(DeliveryHome);
